export const paths = {
  install: '/install',
  auth: '/auth',
  signUp: '/auth/sign-up',
  forgotPassword: '/auth/forgot-password',
  resetPassword: '/confirm-reset-password',
  confirmEmail: '/confirm-email',
  oauthSuccess: '/oauth/success',
  oauthError: '/oauth/error',
  onboarding: '/onboarding',
  /** Первая тренировка знакомства: /onboarding/training?module=<id> (до finish-onboarding, вне AppGate). */
  onboardingTraining: '/onboarding/training',
  /** Итог знакомства: результат, время напоминаний, почта, «На главную». */
  onboardingResult: '/onboarding/result',
  dashboard: '/',
  /** «Слова и модули» (2026-10-23): вкладки «Мои модули» и «Готовые», «Недавно добавленные» и строка создания внизу. */
  words: '/words',
  /** «Слова и модули» на вкладке «Готовые» — бывшие «Публичные модули» (старый адрес /library ведёт сюда). */
  wordsReady: '/words?tab=ready',
  /** «Слова и модули» → «Мои модули» → «Удалённые» (старый адрес /units?filter=deleted ведёт сюда). */
  wordsDeleted: '/words?filter=deleted',
  unit: '/units/:id',
  cram: '/units/:id/cram',
  modulePreview: '/units/:id/preview',
  libraryItem: '/library/:id',
  session: '/session',
  training: '/training',
  stats: '/stats',
  notifications: '/notifications',
  profile: '/profile',
  plans: '/plans',
  community: '/community',
  useful: '/useful',
  /** «Грамматика» — «Тропинка» тем; основной раздел меню (владелец 2026-10-23: «не надо долго искать»). */
  grammar: '/grammar',
  usefulVerbs: '/useful/verbs',
  usefulPhrasal: '/useful/phrasal',
  usefulSounds: '/useful/sounds',
  usefulAlphabet: '/useful/alphabet',
  paymentSuccess: '/payment/success',
  /** Отписка от напоминаний по ссылке из письма — публичная, вне AppGate. */
  unsubscribe: '/unsubscribe',
  /** Цель ссылки из письма-напоминания: /repeat?module=<id>. */
  repeat: '/repeat',
  admin: '/admin',
  /** Урок грамматики — в оболочке. */
  grammarLesson: '/grammar/:slug',
  /** Упражнение грамматики — на весь экран, вне оболочки (как тренировка). */
  grammarExercise: '/grammar/:slug/exercise/:no',
  /** Подборка повторения грамматики (2026-10-25) — на весь экран; сюда же ведут письма «Грамматика к повтору». */
  grammarReview: '/grammar/review',
  /** «Задание дня» с главной: знакомство с 5 новыми словами и тренировка по ним (2026-10-22). */
  dailyTask: '/daily',
  /** «Выживи в Лондоне»: карта и игры (истории, миссия дня, босс). */
  stories: '/stories',
  storyMistakes: '/stories/mistakes',
  /** Игра на весь экран, вне оболочки (как тренировка). */
  storyPlay: '/stories/play/:runId',
  /** Демо миссии без регистрации (2026-10-25): ссылка «Попробовать» с лендинга; вне AppGate. */
  storyDemo: '/demo',
  /** После регистрации из итогов демо: слова, выбранные в итогах, ложатся в модуль «Выживи в Лондоне» (под AppGate). */
  demoWords: '/demo/words',
  cardsWorkspace: '/cards/new',
  topicCards: '/cards/topics',
  unitCardsWorkspace: '/units/:id/cards/new',
} as const;

export const buildUnitPath = (id: number | string) => `/units/${id}`;
export const buildCramPath = (id: number | string) => `/units/${id}/cram`;
export const buildModulePreviewPath = (id: number | string) => `/units/${id}/preview`;
export const buildLibraryItemPath = (id: string) => `/library/${id}`;
export const buildUnitCardsWorkspacePath = (id: number | string) => `/units/${id}/cards/new`;
export const buildStoryPlayPath = (runId: string) => `/stories/play/${runId}`;
/** Вкладка игры со стартом миссии дня — куда ведёт регистрация из демо и роликов (решение владельца 2026-10-28). */
export const STORIES_PLAY_DAILY = '/stories?play=daily';
export const buildGrammarLessonPath = (slug: string) => `/grammar/${encodeURIComponent(slug)}`;
export const buildGrammarExercisePath = (slug: string, no: number) => `/grammar/${encodeURIComponent(slug)}/exercise/${no}`;

/**
 * Переход на «Слова и модули» со строкой создания в фокусе (`navigate(paths.words, { state })`): модуль и слово —
 * по желанию. Страницы «Из словаря» больше нет (решение владельца 2026-10-21) — «Добавить карточку» и
 * «Разобрать» в модуле, новый модуль, «+ Карточка» и старые адреса /cards/new ведут сюда. До 2026-10-23
 * строка жила на главной.
 */
export interface ComposerNavigationState {
  focusComposer: true;
  composerModuleId?: string;
  composerWord?: string;
}

/** Возврат на «Слова и модули» из тренировки или выбора тренировки — с модулем, который тренировали (2026-10-28):
 *  без него страница выбрала бы первый модуль. */
export interface WordsReturnState {
  wordsModuleId: string;
}

export const buildComposerState = (moduleId?: string | null, word?: string | null): ComposerNavigationState => ({
  focusComposer: true,
  ...(moduleId ? { composerModuleId: moduleId } : {}),
  ...(word ? { composerWord: word } : {}),
});
